/* ==========================================================================
   Life Planner — Bundle Builder
   Motor único para configuradores de bundle en Shopify (Online Store 2.0).
   Sin dependencias. Productos y stock reales del catálogo.

   Liquid renderiza el armazón (media, cabecera, garantías) y deja un
   [data-bb-root] vacío. Este componente renderiza dentro de ese nodo:
   selector de tier o progreso · filtros · grid · resumen · CTA.
   ========================================================================== */
(function () {
  'use strict';

  /* ----------------------------------------------------------------------
     Utilidades
     ---------------------------------------------------------------------- */

  /* Toda la red del configurador pasa por aquí: una petición que no responde
     tiene que fallar, no quedarse colgada bloqueando la compra. */
  var REQUEST_TIMEOUT = 12000;

  var fetchWithTimeout = function (url, options, timeout) {
    var opts = options || {};
    if (typeof AbortController === 'undefined') return fetch(url, opts);
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, timeout || REQUEST_TIMEOUT);
    opts.signal = controller.signal;
    return fetch(url, opts).then(
      function (res) { clearTimeout(timer); return res; },
      function (err) { clearTimeout(timer); throw err; }
    );
  };

  /* Shopify devuelve HTML en algunos errores (429, 5xx). Sin esto el cliente
     vería "Unexpected token <" en vez de un mensaje. */
  var readJson = function (res) {
    return res.json().catch(function () { return {}; });
  };

  var uid = function (prefix) {
    return (prefix || 'bb') + '-' + Date.now().toString(36) + '-' +
      Math.random().toString(36).slice(2, 8);
  };

  var esc = function (value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  var handleize = function (value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  var unique = function (list) {
    var seen = Object.create(null);
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var key = String(list[i]);
      if (!seen[key]) { seen[key] = true; out.push(list[i]); }
    }
    return out;
  };

  var prettyVariant = function (title) {
    if (!title || title === 'Default Title') return '';
    return title.split(' / ').join(' · ');
  };

  /* Formateo de dinero compatible con los money_format de Shopify. */
  var formatMoney = function (cents, format) {
    var value = Number(cents);
    if (!isFinite(value)) value = 0;
    var pattern = format || '€{{amount_with_comma_separator}}';

    var withDelimiters = function (number, precision, thousands, decimal) {
      var amount = (number / 100.0).toFixed(precision);
      var parts = amount.split('.');
      var head = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var tail = parts[1] ? decimal + parts[1] : '';
      return head + tail;
    };

    return pattern.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, name) {
      switch (name) {
        case 'amount': return withDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        case 'amount_with_space_separator': return withDelimiters(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(value, 0, ' ', ',');
        default: return '';
      }
    });
  };

  /* Cantidad máxima vendible de una variante según el inventario real. */
  var maxQtyForVariant = function (variant) {
    if (!variant || variant.available === false) return 0;
    var tracked = variant.inventory_management === 'shopify';
    var denies = variant.inventory_policy === 'deny';
    if (tracked && denies) {
      var qty = Number(variant.inventory_quantity);
      if (!isFinite(qty)) return 9999;
      return qty > 0 ? qty : 0;
    }
    return 9999;
  };

  /* ----------------------------------------------------------------------
     Componente
     ---------------------------------------------------------------------- */

  var DEFAULT_STRINGS = {
    createLabel: 'Crea il tuo bundle:',
    next: 'Continua',
    back: 'Indietro',
    addToCart: 'Aggiungi al carrello',
    adding: 'Aggiunta in corso…',
    added: 'Bundle aggiunto al carrello',
    priceLabel: 'Prezzo bundle',
    pricePlaceholder: 'Da definire',
    all: 'Tutti',
    soldOut: 'Esaurito',
    available: 'Disponibile',
    lastUnits: 'Ultime {n}',
    add: 'Aggiungi',
    selected: 'Selezionato',
    emptyCollection: 'Nessun prodotto disponibile in questa categoria.',
    stepCounter: '{n} / {max} selezionati',
    hintRemaining: 'Scegline ancora {n} per completare il tuo bundle.',
    hintComplete: 'Il tuo bundle è completo.',
    ctaIncomplete: 'Seleziona ancora {n}',
    summaryTitle: 'La tua configurazione',
    remove: 'Rimuovi',
    saving: 'Risparmi {amount} con il bundle.',
    unavailableCta: 'Bundle non disponibile',
    unavailableHint: '{items}: al momento esaurito. Questo bundle torna disponibile appena rientra.',
    stockChanged: 'La disponibilità di alcuni prodotti è cambiata. Rivedi la selezione.',
    genericError: 'Non è stato possibile aggiungere il bundle. Riprova.',
    preorderBadge: 'PREORDINE',
    preorderItemNote: 'In preordine · spedizione dal 30 settembre',
    preorderBar: 'Il tuo bundle contiene un articolo in preordine: spedizione dal 30 settembre.',
    preorderNote: "Spedizioni a partire dal 30 settembre. Se acquisti un prodotto disponibile insieme a un articolo in preordine, l'intero ordine verrà spedito in un'unica soluzione quando tutti i prodotti saranno disponibili.",
    swipeHint: 'Scorri per vedere tutte le {n}',
    railLabel: 'Prodotti del passaggio, scorri in orizzontale',
    preorderProperty: 'Spedizione',
    preorderPropertyValue: 'dal 30 settembre (preordine)'
  };

  /* Mismo icono del aviso de la ficha de producto, para que el bloque se lea
     como el mismo mensaje en los dos sitios. */
  var TRUCK = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false">' +
    '<path fill-rule="evenodd" d="M4 5.25a.75.75 0 0 1 .75-.75h6.991a2.75 2.75 0 0 1 2.645 1.995l.427 1.494a.25.25 0 0 0 .18.173l1.681.421a1.75 1.75 0 0 1 1.326 1.698v1.219a1.75 1.75 0 0 1-1.032 1.597 2.5 2.5 0 1 1-4.955.153h-3.025a2.5 2.5 0 1 1-4.78-.75h-.458a.75.75 0 0 1 0-1.5h2.5c.03 0 .06.002.088.005a2.493 2.493 0 0 1 1.947.745h4.43a2.493 2.493 0 0 1 1.785-.75c.698 0 1.33.286 1.783.748a.25.25 0 0 0 .217-.248v-1.22a.25.25 0 0 0-.19-.242l-1.682-.42a1.75 1.75 0 0 1-1.258-1.217l-.427-1.494a1.25 1.25 0 0 0-1.202-.907h-6.991a.75.75 0 0 1-.75-.75Zm2.5 9.25a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm8 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"></path>' +
    '<path d="M3.25 8a.75.75 0 0 0 0 1.5h5a.75.75 0 0 0 0-1.5h-5Z"></path></svg>';

  /* ----------------------------------------------------------------------
     Preordine

     Un articolo è in preordine quando lo dice il piano di vendita dell'app
     (fonte autorevole) oppure, in mancanza, quando l'inventario continua a
     vendere con giacenza esaurita. La seconda regola si può spegnere dal
     tema, per i negozi che sovravendono senza essere in preordine.
     ---------------------------------------------------------------------- */

  /* Piano di preordine di una variante, o null. */
  var preorderPlan = function (variant, product, groupName) {
    if (!variant) return null;
    var wanted = handleize(groupName || '');
    if (!wanted) return null;
    var plans = variant.selling_plans || [];
    var groups = (product && product.selling_plan_groups) || [];
    for (var i = 0; i < plans.length; i++) {
      var plan = plans[i];
      if (!plan || !plan.id) continue;
      if (handleize(plan.name || '') === wanted) return plan;
      for (var g = 0; g < groups.length; g++) {
        if (String(groups[g].id) === String(plan.group) &&
            handleize(groups[g].name || '') === wanted) return plan;
      }
    }
    return null;
  };

  /* Giacenza reale e verificabile. Con merce in magazzino non c'è
     preordine, qualunque piano di vendita resti attaccato al prodotto. */
  var hasRealStock = function (variant) {
    return !!variant &&
      variant.inventory_management === 'shopify' &&
      Number(variant.inventory_quantity) > 0;
  };

  /* Inventario che vende a zero: preordine di fatto. */
  var sellsWithoutStock = function (variant) {
    return !!variant &&
      variant.inventory_management === 'shopify' &&
      variant.inventory_policy === 'continue' &&
      Number(variant.inventory_quantity) <= 0;
  };

  class BundleBuilder extends HTMLElement {}

  var proto = BundleBuilder.prototype;

  /* ---------- ciclo de vida ---------- */

  proto.connectedCallback = function () {
    if (this.initialized) return;
    this.initialized = true;

    this.config = this.parseConfig();
    if (!this.config) return;

    /* Un campo de texto vaciado en el editor llega como "" o null: no debe
       machacar el valor por defecto. */
    var custom = {};
    var provided = this.config.strings || {};
    Object.keys(provided).forEach(function (key) {
      if (provided[key] != null && String(provided[key]).trim() !== '') custom[key] = provided[key];
    });
    this.strings = Object.assign({}, DEFAULT_STRINGS, custom);
    this.bundleRef = uid('bundle');
    this.isSubmitting = false;
    this.error = '';
    this.notice = '';

    this.stepIndex = 0;
    this.tierIndex = this.config.tiers.length ? (this.config.defaultTier || 0) : -1;
    this.filters = {};          // stepId -> { groupIndex: value }
    this.variantChoice = {};    // stepId -> productId -> variantId
    this.railPos = {};          // stepId -> scrollLeft del carril
    this.selection = {};        // stepId -> [{ productId, variantId, qty }]

    var self = this;
    this.config.steps.forEach(function (step) {
      self.filters[step.id] = {};
      self.variantChoice[step.id] = {};
      self.selection[step.id] = [];
    });
    if (this.tierIndex > -1) this.applyTier(this.tierIndex);

    this.onClick = this.handleClick.bind(this);
    this.addEventListener('click', this.onClick);

    this.onRailScroll = this.handleRailScroll.bind(this);
    this.addEventListener('scroll', this.onRailScroll, { capture: true, passive: true });

    /* El catálogo se serializa al renderizar la página. Si el cliente la deja
       abierta, esos números envejecen: al volver a la pestaña se releen. */
    this.onVisible = this.handleVisible.bind(this);
    document.addEventListener('visibilitychange', this.onVisible);
    window.addEventListener('focus', this.onVisible);

    this.render();
    this.hydrateOverflow();
  };

  proto.disconnectedCallback = function () {
    this.removeEventListener('scroll', this.onRailScroll, { capture: true });
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('visibilitychange', this.onVisible);
    window.removeEventListener('focus', this.onVisible);
    this.initialized = false;
  };

  proto.handleVisible = function () {
    if (document.visibilityState === 'hidden') return;
    this.refreshStock();
  };

  /* Relee el stock vivo de lo que está a la vista y de lo ya seleccionado.
     Limitado y con freno para no bombardear la tienda. */
  proto.refreshStock = function () {
    /* Con un envío en marcha, los items ya están calculados: recortar aquí la
       selección dejaría la pantalla diciendo algo distinto de lo comprado. */
    if (this.isSubmitting) return;
    var self = this;
    var now = Date.now();
    if (this.refreshing || (this.lastRefresh && now - this.lastRefresh < 60000)) return;
    this.lastRefresh = now;

    var step = this.step();
    var handles = [];
    this.config.steps.forEach(function (item) {
      self.selection[item.id].forEach(function (entry) {
        if (entry.productHandle) handles.push(entry.productHandle);
      });
    });
    this.filteredProducts(step).forEach(function (product) {
      if (product.handle) handles.push(product.handle);
    });
    handles = unique(handles).slice(0, 20);
    if (!handles.length) return;

    this.refreshing = true;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    Promise.all(handles.map(function (handle) {
      return fetchWithTimeout(root + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.ok ? res.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      var fresh = Object.create(null);
      products.forEach(function (product) {
        if (!product || !product.variants) return;
        product.variants.forEach(function (variant) { fresh[String(variant.id)] = variant; });
      });
      if (!Object.keys(fresh).length) return;

      /* Actualiza el catálogo en memoria. */
      self.config.steps.forEach(function (item) {
        item.products.forEach(function (product) {
          product.variants.forEach(function (variant) {
            var live = fresh[String(variant.id)];
            if (!live) return;
            variant.available = live.available !== false;
            if (live.inventory_management !== undefined) variant.inventory_management = live.inventory_management;
            if (live.inventory_policy !== undefined) variant.inventory_policy = live.inventory_policy;
            if (live.inventory_quantity !== undefined) variant.inventory_quantity = live.inventory_quantity;
            /* /products/{handle}.js no expone el inventario, pero sí los
               planes de venta: es la única parte que se puede revalidar. */
            if (live.selling_plan_allocations) {
              variant.selling_plans = live.selling_plan_allocations.map(function (allocation) {
                return {
                  id: allocation.selling_plan_id || (allocation.selling_plan || {}).id,
                  name: (allocation.selling_plan || {}).name || '',
                  group: allocation.selling_plan_group_id
                };
              });
            }
          });
        });
      });

      /* Recorta lo seleccionado que ya no cabe en el stock. */
      var trimmed = [];
      self.config.steps.forEach(function (item) {
        self.selection[item.id] = self.selection[item.id].filter(function (entry) {
          var live = fresh[String(entry.variantId)];
          if (!live) return true;
          var cap = maxQtyForVariant({
            available: live.available !== false,
            inventory_management: live.inventory_management,
            inventory_policy: live.inventory_policy,
            inventory_quantity: live.inventory_quantity
          });
          if (cap <= 0) { trimmed.push(entry.productTitle); return false; }
          if (entry.qty > cap) { entry.qty = cap; trimmed.push(entry.productTitle); }
          return true;
        });
      });

      if (trimmed.length) {
        self.error = self.strings.stockChanged;
        /* Devuelve al cliente al primer paso que ha quedado incompleto,
           para que vea dónde está el hueco en vez de un CTA apagado. */
        for (var i = 0; i < self.config.steps.length; i++) {
          if (!self.isStepValid(self.config.steps[i])) { self.stepIndex = i; break; }
        }
      }
      self.render();
    }).catch(function () { /* el catálogo en pantalla sigue siendo utilizable */ })
      .then(function () { self.refreshing = false; });
  };

  proto.parseConfig = function () {
    var node = this.querySelector('script[data-bb-config]');
    if (!node) {
      console.warn('[bundle-builder] configuración ausente');
      return null;
    }
    var data;
    try {
      data = JSON.parse(node.textContent);
    } catch (err) {
      console.error('[bundle-builder] JSON inválido', err);
      return null;
    }

    data.mode = data.mode === 'free' ? 'free' : 'steps';
    /* Un descuento configurado no tiene sentido sin precio a la vista. */
    if (data.discountType && data.discountType !== 'none' && Number(data.discountValue) > 0) {
      data.showPrice = true;
    }
    data.tiers = (data.tiers || []).filter(function (tier) {
      return tier && Number(tier.qty) > 0;
    });
    data.steps = (data.steps || []).filter(function (step) {
      return step && Array.isArray(step.products);
    });

    data.steps.forEach(function (step, index) {
      step.id = step.id || ('step-' + index);
      step.min = Number(step.min) > 0 ? Number(step.min) : 1;
      step.max = Number(step.max) >= step.min ? Number(step.max) : step.min;
      step.allowRepeat = step.allowRepeat === true;
      /* 'variant': una tarjeta por combinación (Puntinato Viola, Righe Giallo…).
         'product': una tarjeta por producto con selectores de opzione. */
      step.cardLevel = step.cardLevel === 'variant' ? 'variant'
        : (step.cardLevel === 'product' ? 'product' : (data.mode === 'free' ? 'variant' : 'product'));
      step.filters = (step.filters || []).filter(function (group) {
        return group && group.label;
      });
      step.products.forEach(function (product) {
        product.options = product.options || [];
        product.tags = product.tags || [];
        product.variants = (product.variants || []).map(function (variant) {
          variant.options = variant.options || [];
          variant.price = Number(variant.price) || 0;
          return variant;
        });
      });
      step.filters.forEach(function (group) {
        if (group.values && group.values.length) return;
        group.values = deriveValues(step, group);
      });
    });

    if (!data.steps.length) {
      console.warn('[bundle-builder] sin pasos configurados');
      return null;
    }
    /* Los tiers solo tienen sentido sobre un pool único. */
    if (data.steps.length > 1) data.tiers = [];
    return data;
  };

  function deriveValues(step, group) {
    var values = [];
    step.products.forEach(function (product) {
      if (group.match === 'tag') {
        values = values.concat(product.tags);
      } else if (group.match === 'variant_option') {
        var index = product.options.indexOf(group.option);
        if (index === -1) return;
        product.variants.forEach(function (variant) {
          if (variant.options[index]) values.push(variant.options[index]);
        });
      }
    });
    return unique(values);
  }

  /* Liquid rinde como máximo 50 productos por colección. Si hay más, se
     completan vía Ajax API. Esas variantes llegan sin inventory_quantity,
     pero la revalidación previa al carrito usa /products/{handle}.js, que sí
     lo trae: el stock nunca se sobrevende. */
  proto.hydrateOverflow = function () {
    var self = this;
    var pending = this.config.steps.filter(function (step) {
      return step.collectionHandle && Number(step.productCount) > step.products.length;
    });
    if (!pending.length) return;

    Promise.all(pending.map(function (step) {
      var known = Object.create(null);
      step.products.forEach(function (product) { known[String(product.id)] = true; });

      var pages = [];
      var totalPages = Math.min(Math.ceil(Number(step.productCount) / 250), 4);
      for (var page = 1; page <= totalPages; page++) pages.push(page);

      return Promise.all(pages.map(function (page) {
        return fetchWithTimeout('/collections/' + step.collectionHandle + '/products.json?limit=250&page=' + page,
          { headers: { Accept: 'application/json' } })
          .then(function (res) { return res.ok ? res.json() : null; })
          .catch(function () { return null; });
      })).then(function (payloads) {
        payloads.forEach(function (payload) {
          if (!payload || !payload.products) return;
          payload.products.forEach(function (raw) {
            if (known[String(raw.id)]) return;
            known[String(raw.id)] = true;
            step.products.push({
              id: raw.id,
              title: raw.title,
              handle: raw.handle,
              url: '/products/' + raw.handle,
              image: (raw.images && raw.images[0]) ? raw.images[0].src : '',
              tags: raw.tags || [],
              selling_plan_groups: (raw.selling_plan_groups || []).map(function (group) {
                return { id: group.id, name: group.name || '' };
              }),
              options: (raw.options || []).map(function (opt) {
                return typeof opt === 'string' ? opt : opt.name;
              }),
              variants: (raw.variants || []).map(function (variant) {
                return {
                  id: variant.id,
                  title: variant.title,
                  available: variant.available !== false,
                  price: Math.round(parseFloat(variant.price) * 100) || 0,
                  options: [variant.option1, variant.option2, variant.option3]
                    .filter(function (value) { return value != null; }),
                  inventory_management: '',
                  inventory_policy: '',
                  inventory_quantity: 0,
                  selling_plans: (variant.selling_plan_allocations || []).map(function (allocation) {
                    return {
                      id: allocation.selling_plan_id || (allocation.selling_plan || {}).id,
                      name: (allocation.selling_plan || {}).name || '',
                      group: allocation.selling_plan_group_id
                    };
                  }),
                  image: variant.featured_image ? variant.featured_image.src : null
                };
              })
            });
          });
        });
        step.filters.forEach(function (group) {
          if (!group.values || !group.values.length) group.values = deriveValues(step, group);
        });
      });
    })).then(function () { self.render(); })
      .catch(function () { /* el catálogo renderizado por Liquid sigue siendo válido */ });
  };

  /* ---------- preordine ---------- */

  /* Devuelve el plan de preventa aplicable a la variante, o null. */
  proto.planFor = function (variant, product) {
    /* Arrivata la merce si vende normalmente: niente piano di preordine,
       quindi niente distintivo, niente avvisi e niente selling_plan nel carrello. */
    if (hasRealStock(variant)) return null;
    return preorderPlan(variant, product, this.config.preorderGroup);
  };

  proto.isPreorder = function (variant, product) {
    if (this.planFor(variant, product)) return true;
    return this.config.preorderFromStock !== false && sellsWithoutStock(variant);
  };

  /* ¿Hay ya un artículo en preventa dentro del bundle? */
  proto.hasPreorderSelected = function () {
    var found = false;
    var self = this;
    this.config.steps.forEach(function (step) {
      (self.selection[step.id] || []).forEach(function (entry) {
        if (entry.preorder) found = true;
      });
    });
    return found;
  };

  /* ¿Este paso ofrece artículos en preventa? Sirve para avisar antes de
     que el cliente elija, no solo después. */
  proto.stepHasPreorder = function (step) {
    var self = this;
    var items = this.stepItems(step);
    for (var i = 0; i < items.length; i++) {
      if (self.isPreorder(items[i].variant, items[i].product)) return true;
    }
    return false;
  };

  /* ---------- lógica de selección ---------- */

  proto.step = function (index) {
    return this.config.steps[index == null ? this.stepIndex : index];
  };

  proto.countFor = function (stepId) {
    return this.selection[stepId].reduce(function (total, entry) {
      return total + entry.qty;
    }, 0);
  };

  proto.isStepValid = function (step) {
    var count = this.countFor(step.id);
    return count >= step.min && count <= step.max;
  };

  proto.isComplete = function () {
    var self = this;
    return this.config.steps.every(function (step) { return self.isStepValid(step); });
  };

  proto.totalRemaining = function () {
    var self = this;
    return this.config.steps.reduce(function (total, step) {
      return total + Math.max(0, step.min - self.countFor(step.id));
    }, 0);
  };

  /* Pasos obligatorios sin una sola unidad vendible: el bundle no se puede
     armar y hay que decirlo, en vez de dejar el botón apagado sin motivo.
     Se mira el catálogo entero del paso, no lo que dejan ver los filtros. */
  proto.unavailableSteps = function () {
    return this.config.steps.filter(function (step) {
      if (step.min < 1) return false;
      return !step.products.some(function (product) {
        return product.variants.some(function (variant) {
          return maxQtyForVariant(variant) > 0;
        });
      });
    });
  };

  proto.usesSteppers = function (step) {
    return step.allowRepeat || step.max > 1;
  };

  /* El tier fija cuántos artículos pide el pool. */
  proto.applyTier = function (index) {
    var tier = this.config.tiers[index];
    if (!tier) return;
    var step = this.config.steps[0];
    var qty = Number(tier.qty);
    step.min = qty;
    step.max = tier.open ? Math.max(qty, 12) : qty;

    /* Si la nueva cuota es menor, recorta desde el final. */
    var over = this.countFor(step.id) - step.max;
    while (over > 0 && this.selection[step.id].length) {
      var last = this.selection[step.id][this.selection[step.id].length - 1];
      var take = Math.min(over, last.qty);
      last.qty -= take;
      over -= take;
      if (last.qty <= 0) this.selection[step.id].pop();
    }
  };

  /* Variantes de un producto que pasan los filtros de tipo variant_option. */
  proto.matchedVariants = function (product, step) {
    var active = this.filters[step.id] || {};
    return product.variants.filter(function (variant) {
      for (var i = 0; i < step.filters.length; i++) {
        var group = step.filters[i];
        var value = active[i];
        if (!value || group.match !== 'variant_option') continue;
        var index = product.options.indexOf(group.option);
        if (index === -1) return false;
        if (handleize(variant.options[index]) !== handleize(value)) return false;
      }
      return true;
    });
  };

  proto.filteredProducts = function (step) {
    var self = this;
    var active = this.filters[step.id] || {};
    return step.products.filter(function (product) {
      for (var i = 0; i < step.filters.length; i++) {
        var group = step.filters[i];
        var value = active[i];
        if (!value) continue;
        if (group.match === 'tag') {
          var tags = product.tags.map(handleize);
          if (tags.indexOf(handleize(value)) === -1) return false;
        } else if (group.match === 'title') {
          if (product.title.toLowerCase().indexOf(String(value).toLowerCase()) === -1) return false;
        }
      }
      return self.matchedVariants(product, step).length > 0;
    });
  };

  /* Tarjetas del paso: por producto o por variante según cardLevel. */
  proto.stepItems = function (step) {
    var self = this;
    var items = [];
    this.filteredProducts(step).forEach(function (product) {
      if (step.cardLevel === 'variant') {
        self.matchedVariants(product, step).forEach(function (variant) {
          items.push({ product: product, variant: variant });
        });
      } else {
        var variant = self.currentVariant(product, step);
        if (variant) items.push({ product: product, variant: variant });
      }
    });
    return items;
  };

  proto.currentVariant = function (product, step) {
    var candidates = this.matchedVariants(product, step);
    if (!candidates.length) return null;
    var chosenId = this.variantChoice[step.id][product.id];
    var found = null;
    if (chosenId) {
      found = candidates.filter(function (v) { return String(v.id) === String(chosenId); })[0] || null;
    }
    if (!found) {
      found = candidates.filter(function (v) { return maxQtyForVariant(v) > 0; })[0] || candidates[0];
      this.variantChoice[step.id][product.id] = found.id;
    }
    return found;
  };

  proto.entryFor = function (stepId, variantId) {
    return this.selection[stepId].filter(function (entry) {
      return String(entry.variantId) === String(variantId);
    })[0] || null;
  };

  proto.qtyFor = function (stepId, variantId) {
    var entry = this.entryFor(stepId, variantId);
    return entry ? entry.qty : 0;
  };

  proto.setQty = function (step, product, variant, qty) {
    var stock = maxQtyForVariant(variant);
    var others = this.countFor(step.id) - this.qtyFor(step.id, variant.id);
    var room = step.max - others;
    var next = Math.max(0, Math.min(qty, stock, room));

    this.selection[step.id] = this.selection[step.id].filter(function (entry) {
      return String(entry.variantId) !== String(variant.id);
    });
    if (next > 0) {
      this.selection[step.id].push({
        productId: product.id,
        productHandle: product.handle,
        productTitle: product.title,
        variantId: variant.id,
        variantTitle: variant.title,
        price: variant.price,
        image: variant.image || product.image || '',
        preorder: this.isPreorder(variant, product),
        sellingPlanId: (this.planFor(variant, product) || {}).id || null,
        qty: next
      });
    }
    this.error = '';
  };

  proto.toggleProduct = function (step, product, variant) {
    if (!variant) variant = this.currentVariant(product, step);
    if (!variant || maxQtyForVariant(variant) < 1) return;

    if (this.qtyFor(step.id, variant.id) > 0) {
      this.setQty(step, product, variant, 0);
      return;
    }
    if (step.max === 1) {
      this.selection[step.id] = [];
    } else if (this.countFor(step.id) >= step.max) {
      return;
    }
    this.setQty(step, product, variant, 1);
  };

  proto.chooseOption = function (step, product, optionIndex, value) {
    var candidates = this.matchedVariants(product, step);
    if (!candidates.length) return;

    var current = this.currentVariant(product, step);
    var target = (current ? current.options.slice() : candidates[0].options.slice());
    target[optionIndex] = value;

    var exact = candidates.filter(function (variant) {
      return variant.options.every(function (opt, i) {
        return handleize(opt) === handleize(target[i]);
      });
    })[0];

    var next = exact || candidates.filter(function (variant) {
      return handleize(variant.options[optionIndex]) === handleize(value) &&
        maxQtyForVariant(variant) > 0;
    })[0] || candidates.filter(function (variant) {
      return handleize(variant.options[optionIndex]) === handleize(value);
    })[0];

    if (!next) return;

    var previousQty = current ? this.qtyFor(step.id, current.id) : 0;
    if (current && previousQty > 0) this.setQty(step, product, current, 0);
    this.variantChoice[step.id][product.id] = next.id;
    if (previousQty > 0) this.setQty(step, product, next, previousQty);
  };

  /* Precio del bundle. El descuento configurado aquí es lo que ve el cliente;
     el dinero lo descuenta la regla de Shopify o el código aplicado al carrito.
     Ambos deben decir lo mismo. */
  proto.pricing = function () {
    var subtotal = this.totalPrice();
    var type = this.config.discountType;
    var value = Number(this.config.discountValue) || 0;
    var total = subtotal;

    if (value > 0) {
      if (type === 'percent') total = Math.round(subtotal * (100 - Math.min(value, 100)) / 100);
      else if (type === 'amount') total = Math.max(0, subtotal - Math.round(value * 100));
      else if (type === 'fixed') total = Math.round(value * 100);
    }
    if (total > subtotal) total = subtotal;

    return { subtotal: subtotal, total: total, saving: Math.max(0, subtotal - total) };
  };

  proto.totalPrice = function () {
    var self = this;
    return this.config.steps.reduce(function (total, step) {
      return total + self.selection[step.id].reduce(function (sum, entry) {
        return sum + (entry.price * entry.qty);
      }, 0);
    }, 0);
  };

  /* ---------- render ---------- */

  proto.render = function () {
    var root = this.querySelector('[data-bb-root]');
    if (!root) {
      root = document.createElement('div');
      root.setAttribute('data-bb-root', '');
      this.appendChild(root);
    }
    root.innerHTML =
      (this.config.tiers.length ? this.renderTiers() : this.renderProgress()) +
      this.renderStep() +
      this.renderSelection() +
      this.renderCta();

    this.restoreRail();
  };

  /* Elegir un producto repinta la sección entera. Sin esto, el carril
     volvería al primer producto cada vez que el cliente toca una tarjeta. */
  proto.restoreRail = function () {
    var rail = this.querySelector('[data-bb-rail]');
    if (!rail) return;
    var stepId = rail.getAttribute('data-bb-rail');
    var saved = this.railPos[stepId] || 0;
    if (saved > 0) rail.scrollLeft = saved;
    this.updateRail(rail);
  };

  /* Barra de avance y pista de deslizamiento. */
  proto.updateRail = function (rail) {
    var stepId = rail.getAttribute('data-bb-rail');
    var span = rail.scrollWidth - rail.clientWidth;
    var ratio = span > 4 ? rail.scrollLeft / span : 0;
    var visible = span > 4 ? rail.clientWidth / rail.scrollWidth : 1;

    var fill = this.querySelector('[data-bb-railfill="' + stepId + '"]');
    if (fill) {
      fill.style.width = Math.max(12, Math.round(visible * 100)) + '%';
      fill.style.transform = 'translateX(' + (ratio * (100 / Math.max(visible, 0.12) - 100)) + '%)';
    }

    /* La pista sobra en cuanto el cliente ha entendido el gesto. */
    var hint = this.querySelector('[data-bb-railhint="' + stepId + '"]');
    if (hint) hint.classList.toggle('is-off', ratio > 0.02);
  };

  proto.handleRailScroll = function (event) {
    var rail = event.target;
    if (!rail || !rail.getAttribute || !rail.hasAttribute('data-bb-rail')) return;
    this.railPos[rail.getAttribute('data-bb-rail')] = rail.scrollLeft;
    this.updateRail(rail);
  };

  /* Panel de tiers: cuántos artículos lleva el bundle y a qué precio. */
  proto.renderTiers = function () {
    var self = this;
    var tiers = this.config.tiers;
    var active = this.tierIndex;
    var last = tiers.length - 1;
    var fill = last > 0 ? (active / last) * 100 : 100;

    var html = '<div class="bb__panel-block">';
    html += '<p class="bb__panel-label">' + esc(this.strings.createLabel) + '</p>';
    html += '<div class="bb__tier" role="radiogroup" aria-label="' + esc(this.strings.createLabel) + '">';

    html += '<div class="bb__tier-row">';
    tiers.forEach(function (tier, i) {
      html += '<button type="button" role="radio" aria-checked="' + (i === active ? 'true' : 'false') +
        '" class="bb__tier-col' + (i === active ? ' is-on' : '') + '" data-bb-tier="' + i + '">' +
        (tier.badge ? '<span class="bb__pill">' + esc(tier.badge) + '</span>' : '<span class="bb__pill-gap"></span>') +
        '<span class="bb__tier-name">' + esc(tier.label) + '</span>' +
        '</button>';
    });
    html += '</div>';

    html += '<div class="bb__rail" aria-hidden="true"><span class="bb__rail-bg"></span>' +
      '<span class="bb__rail-fill" style="width:' + fill + '%"></span>';
    tiers.forEach(function (tier, i) {
      var pos = last > 0 ? (i / last) * 100 : 50;
      html += '<span class="bb__dot' + (i <= active ? ' is-on' : '') + '" style="left:' + pos + '%"></span>';
    });
    html += '</div>';

    html += '<div class="bb__tier-row">';
    tiers.forEach(function (tier, i) {
      html += '<button type="button" tabindex="-1" aria-hidden="true" class="bb__tier-meta' +
        (i === active ? ' is-on' : '') + '" data-bb-tier="' + i + '">';
      if (tier.price) html += '<span class="bb__tier-price">' + esc(tier.price) + '</span>';
      if (tier.note) html += '<span class="bb__tier-note">' + esc(tier.note) + '</span>';
      if (tier.discount) html += '<span class="bb__tier-off">' + esc(tier.discount) + '</span>';
      html += '</button>';
    });
    html += '</div></div></div>';
    return html;
  };

  /* Mismo lenguaje visual que los tiers, pero midiendo el avance por pasos. */
  proto.renderProgress = function () {
    if (this.config.steps.length < 2) return '';
    var self = this;
    var steps = this.config.steps;
    var last = steps.length - 1;
    var fill = last > 0 ? (this.stepIndex / last) * 100 : 100;

    var html = '<div class="bb__panel-block">';
    html += '<p class="bb__panel-label">' + esc(this.strings.createLabel) + '</p>';
    html += '<div class="bb__tier">';

    html += '<div class="bb__tier-row">';
    steps.forEach(function (step, i) {
      var done = self.isStepValid(step);
      html += '<button type="button" class="bb__tier-col' + (i === self.stepIndex ? ' is-on' : '') +
        (done ? ' is-done' : '') + '" data-bb-goto="' + i + '"' +
        ' aria-current="' + (i === self.stepIndex ? 'step' : 'false') + '">' +
        '<span class="bb__pill-gap"></span>' +
        '<span class="bb__tier-name">' + esc(step.itemLabel || step.title || step.label || '') + '</span>' +
        '</button>';
    });
    html += '</div>';

    html += '<div class="bb__rail" aria-hidden="true"><span class="bb__rail-bg"></span>' +
      '<span class="bb__rail-fill" style="width:' + fill + '%"></span>';
    steps.forEach(function (step, i) {
      var pos = last > 0 ? (i / last) * 100 : 50;
      html += '<span class="bb__dot' + (i <= self.stepIndex ? ' is-on' : '') +
        (self.isStepValid(step) ? ' is-done' : '') + '" style="left:' + pos + '%"></span>';
    });
    html += '</div>';

    html += '<div class="bb__tier-row">';
    steps.forEach(function (step, i) {
      var count = self.countFor(step.id);
      html += '<button type="button" tabindex="-1" aria-hidden="true" class="bb__tier-meta' +
        (i === self.stepIndex ? ' is-on' : '') + '" data-bb-goto="' + i + '">' +
        '<span class="bb__tier-note">' + count + ' / ' + step.max + '</span></button>';
    });
    html += '</div></div></div>';
    return html;
  };

  proto.renderStep = function () {
    var step = this.step();
    var items = this.stepItems(step);
    var count = this.countFor(step.id);
    var self = this;

    var html = '<section class="bb__step" role="group" aria-label="' + esc(step.title || '') +
      '" data-bb-step="' + esc(step.id) + '">';

    html += '<div class="bb__step-head">';
    html += '<h3 class="bb__step-title">' +
      (step.label ? '<span class="bb__step-num">' + esc(step.label) + '</span>' : '') +
      esc(step.title || '') + '</h3>';
    html += '<p class="bb__counter" aria-live="polite">' +
      esc(this.strings.stepCounter.replace('{n}', count).replace('{max}', step.max)) + '</p>';
    html += '</div>';

    /* El cliente no puede llegar al carrito sin haber visto la fecha de
       envío: se le avisa en el paso donde puede elegir un preordine y en
       todos los siguientes mientras siga en el bundle. */
    if (this.hasPreorderSelected() || this.stepHasPreorder(step)) {
      html += '<p class="bb__prebar" role="status">' +
        '<span class="bb__prebar-tag">' + esc(this.strings.preorderBadge) + '</span>' +
        esc(this.strings.preorderBar) + '</p>';
    }

    if (step.filters.length) {
      html += '<div class="bb__filters">';
      step.filters.forEach(function (group, groupIndex) {
        var activeValue = self.filters[step.id][groupIndex] || '';
        html += '<div class="bb__filter-group" role="group" aria-label="' + esc(group.label) + '">';
        html += '<span class="bb__filter-label">' + esc(group.label) + '</span>';
        html += '<div class="bb__chips">';
        html += '<button type="button" class="bb__chip' + (activeValue ? '' : ' is-on') +
          '" aria-pressed="' + (activeValue ? 'false' : 'true') +
          '" data-bb-filter="' + groupIndex + '" data-bb-value="">' + esc(self.strings.all) + '</button>';
        group.values.forEach(function (value) {
          var isOn = handleize(activeValue) === handleize(value);
          html += '<button type="button" class="bb__chip' + (isOn ? ' is-on' : '') +
            '" aria-pressed="' + (isOn ? 'true' : 'false') +
            '" data-bb-filter="' + groupIndex + '" data-bb-value="' + esc(value) + '">' +
            esc(value) + '</button>';
        });
        html += '</div></div>';
      });
      html += '</div>';
    }

    if (!items.length) {
      html += '<p class="bb__empty">' + esc(this.strings.emptyCollection) + '</p>';
    } else {
      /* En móvil la parrilla vertical obliga a recorrer decenas de productos
         antes de llegar al botón. A partir de cinco, el paso se convierte en
         un carril horizontal de dos columnas. Con cuatro o menos no hay nada
         que deslizar y la parrilla de siempre es mejor. */
      var rail = items.length > 4;
      html += '<div class="bb__grid' + (rail ? ' bb__grid--rail' : '') + '"' +
        (rail ? ' data-bb-rail="' + esc(step.id) + '" role="group" aria-label="' +
          esc(this.strings.railLabel) + '"' : '') + '>';
      items.forEach(function (item) { html += self.renderCard(item, step); });
      html += '</div>';
      if (rail) {
        html += '<div class="bb__rail-foot">' +
          '<span class="bb__rail-track" aria-hidden="true">' +
            '<span class="bb__rail-fill" data-bb-railfill="' + esc(step.id) + '"></span>' +
          '</span>' +
          '<span class="bb__rail-hint" data-bb-railhint="' + esc(step.id) + '">' +
            esc(this.strings.swipeHint.replace('{n}', items.length)) +
            '<span class="bb__rail-arrow" aria-hidden="true">&rarr;</span>' +
          '</span>' +
        '</div>';
      }
    }

    html += '</section>';
    return html;
  };

  proto.renderCard = function (item, step) {
    var product = item.product;
    var variant = item.variant;
    if (!variant) return '';

    var byVariant = step.cardLevel === 'variant';
    var stock = maxQtyForVariant(variant);
    var qty = this.qtyFor(step.id, variant.id);
    var selected = qty > 0;
    var stepper = this.usesSteppers(step);
    var candidates = byVariant ? [variant] : this.matchedVariants(product, step);
    var variantName = prettyVariant(variant.title);
    var tracked = variant.inventory_management === 'shopify';

    var classes = ['bb__tile'];
    if (selected) classes.push('is-on');
    if (stock < 1) classes.push('is-out');

    var html = '<article class="' + classes.join(' ') + '" data-bb-product="' + esc(product.id) +
      '" data-bb-variant="' + esc(variant.id) + '">';

    /* Distintivo flotante solo para excepciones que importan a la venta. */
    var preorder = this.isPreorder(variant, product);
    if (stock < 1) {
      html += '<span class="bb__flag bb__flag--out">' + esc(this.strings.soldOut) + '</span>';
    } else if (preorder) {
      /* El preventa manda sobre "ultime N": la fecha de envío pesa más en la
         decisión que las unidades restantes. */
      html += '<span class="bb__flag bb__flag--pre">' + esc(this.strings.preorderBadge) + '</span>';
    } else if (tracked && stock <= 3) {
      html += '<span class="bb__flag bb__flag--low">' +
        esc(this.strings.lastUnits.replace('{n}', stock)) + '</span>';
    }

    html += '<div class="bb__tile-media">';
    if (variant.image || product.image) {
      html += '<img class="bb__tile-img" src="' + esc(variant.image || product.image) +
        '" alt="' + esc(product.title + (variantName ? ' ' + variantName : '')) +
        '" loading="lazy" width="400" height="400">';
    } else {
      html += '<span class="bb__tile-img bb__tile-img--none" aria-hidden="true"></span>';
    }
    html += '</div>';

    html += '<div class="bb__tile-body">';
    html += '<p class="bb__tile-name">' + esc(product.title) +
      (byVariant && variantName ? '<span class="bb__tile-variant">' + esc(variantName) + '</span>' : '') +
      '</p>';
    if (preorder && stock >= 1) {
      html += '<p class="bb__tile-pre">' + esc(this.strings.preorderItemNote) + '</p>';
    }

    if (!byVariant && product.options.length && candidates.length > 1) {
      product.options.forEach(function (optionName, optionIndex) {
        var values = unique(candidates.map(function (v) { return v.options[optionIndex]; })
          .filter(function (v) { return v; }));
        if (values.length < 2) return;
        html += '<div class="bb__swatches" role="group" aria-label="' + esc(optionName) + '">';
        values.forEach(function (value) {
          var reachable = candidates.some(function (v) {
            return handleize(v.options[optionIndex]) === handleize(value) && maxQtyForVariant(v) > 0;
          });
          var isOn = handleize(variant.options[optionIndex]) === handleize(value);
          html += '<button type="button" class="bb__swatch' + (isOn ? ' is-on' : '') +
            (reachable ? '' : ' is-off') + '"' + (reachable ? '' : ' disabled') +
            ' aria-pressed="' + (isOn ? 'true' : 'false') + '"' +
            ' title="' + esc(optionName + ': ' + value) + '"' +
            ' data-bb-option="' + optionIndex + '" data-bb-value="' + esc(value) + '">' +
            esc(value) + '</button>';
        });
        html += '</div>';
      });
    }

    html += '<div class="bb__tile-slot">';
    if (stock < 1) {
      html += '<span class="bb__tile-status">' + esc(this.strings.soldOut) + '</span>';
    } else if (stepper) {
      var roomLeft = step.max - (this.countFor(step.id) - qty);
      var canAdd = qty < Math.min(stock, roomLeft);
      if (qty < 1) {
        /* Bundle al completo: il pulsante non deve restare cliccabile a vuoto. */
        html += '<button type="button" class="bb__add" data-bb-qty="1"' + (canAdd ? '' : ' disabled') + '>' +
          esc(this.strings.add) + '<span aria-hidden="true">+</span></button>';
      } else {
        html += '<span class="bb__stepper">' +
          '<button type="button" class="bb__sb" data-bb-qty="-1" aria-label="Riduci quantità">−</button>' +
          '<span class="bb__qty" aria-live="polite">' + qty + '</span>' +
          '<button type="button" class="bb__sb" data-bb-qty="1"' + (canAdd ? '' : ' disabled') +
          ' aria-label="Aumenta quantità">+</button></span>';
      }
    } else {
      html += '<button type="button" class="bb__add' + (selected ? ' is-on' : '') +
        '" data-bb-toggle aria-pressed="' + (selected ? 'true' : 'false') + '">' +
        (selected ? esc(this.strings.selected) : esc(this.strings.add) + '<span aria-hidden="true">+</span>') +
        '</button>';
    }
    html += '</div>';

    html += '</div></article>';
    return html;
  };

  /* Resumen compacto: lo elegido, retirable aunque un filtro oculte su tile. */
  proto.renderSelection = function () {
    var self = this;
    var chips = [];
    this.config.steps.forEach(function (step) {
      self.selection[step.id].forEach(function (entry) {
        var name = entry.productTitle;
        var variantName = prettyVariant(entry.variantTitle);
        if (variantName) name += ' · ' + variantName;
        /* El nombre de la categoría sobra si ya lo dice el título del producto. */
        var slot = step.itemLabel || step.title || '';
        if (slot && entry.productTitle.toLowerCase().indexOf(slot.toLowerCase()) === 0) slot = '';
        chips.push('<span class="bb__sel' + (entry.preorder ? ' is-pre' : '') + '"' +
          (entry.preorder ? ' title="' + esc(self.strings.preorderItemNote) + '"' : '') + '>' +
          (slot ? '<span class="bb__sel-slot">' + esc(slot) + '</span>' : '') +
          (entry.preorder ? '<span class="bb__sel-pre">' + esc(self.strings.preorderBadge) + '</span>' : '') +
          esc(name) + (entry.qty > 1 ? ' <em>×' + entry.qty + '</em>' : '') +
          '<button type="button" class="bb__sel-x" aria-label="' +
          esc(self.strings.remove + ' ' + name) + '" data-bb-remove="' + esc(step.id) +
          '" data-bb-remove-variant="' + esc(entry.variantId) + '">×</button></span>');
      });
    });

    if (!chips.length) return '';
    return '<div class="bb__selection"><p class="bb__panel-label">' +
      esc(this.config.summaryTitle || this.strings.summaryTitle) + '</p>' +
      '<div class="bb__sels">' + chips.join('') + '</div></div>';
  };

  proto.renderCta = function () {
    var remaining = this.totalRemaining();
    var complete = this.isComplete();
    var blocked = this.unavailableSteps();
    var ready = complete && !blocked.length && !this.isSubmitting;

    var missing = blocked.map(function (step) {
      return step.itemLabel || step.title || step.label || '';
    }).join(', ');

    var label;
    if (this.isSubmitting) {
      label = this.strings.adding;
    } else if (blocked.length) {
      label = this.strings.unavailableCta;
    } else if (!complete) {
      label = this.strings.ctaIncomplete.replace('{n}', remaining);
    } else {
      label = this.config.addToCartText || this.strings.addToCart;
    }

    var html = '<div class="bb__cta-wrap">';

    if (blocked.length) {
      html += '<p class="bb__hint bb__hint--blocked" role="status">' +
        esc(this.strings.unavailableHint.replace('{items}', missing)) + '</p>';
    } else {
      html += '<p class="bb__hint' + (complete ? ' is-done' : '') + '" aria-live="polite">' +
        esc(complete
          ? this.strings.hintComplete
          : this.strings.hintRemaining.replace('{n}', remaining)) + '</p>';
    }

    if (this.hasPreorderSelected()) {
      html += '<p class="bb__prenote" role="status">' + TRUCK +
        '<span>' + esc(this.strings.preorderNote) + '</span></p>';
    }

    html += '<button type="button" class="bb__cta" data-bb-add' + (ready ? '' : ' disabled') + '>';
    html += '<span class="bb__cta-label">' + esc(label) + '</span>';
    if (complete && !blocked.length && !this.isSubmitting) {
      /* Prezzo spento: non si stampa nulla, nemmeno un segnaposto. */
      if (this.config.showPrice) {
        var money = this.pricing();
        html += '<span class="bb__cta-price">';
        if (money.saving > 0) {
          html += '<s class="bb__cta-was">' + esc(formatMoney(money.subtotal, this.config.moneyFormat)) + '</s> ';
        }
        html += esc(formatMoney(money.total, this.config.moneyFormat));
        html += '</span>';
      }
    }
    html += '</button>';

    if (complete && !blocked.length && this.config.showPrice) {
      var saved = this.pricing().saving;
      if (saved > 0) {
        html += '<p class="bb__saving">' +
          esc(this.strings.saving.replace('{amount}', formatMoney(saved, this.config.moneyFormat))) +
          '</p>';
      }
    }

    if (this.error) html += '<p class="bb__error" role="alert">' + esc(this.error) + '</p>';
    if (this.notice) html += '<p class="bb__notice" role="status">' + esc(this.notice) + '</p>';

    html += '</div>';
    return html;
  };

  /* ---------- eventos ---------- */

  proto.handleClick = function (event) {
    var step = this.step();

    var tier = event.target.closest('[data-bb-tier]');
    if (tier && this.contains(tier)) {
      this.tierIndex = Number(tier.getAttribute('data-bb-tier'));
      this.applyTier(this.tierIndex);
      this.error = '';
      this.render();
      return;
    }

    var goto = event.target.closest('[data-bb-goto]');
    if (goto && this.contains(goto)) {
      var target = Number(goto.getAttribute('data-bb-goto'));
      if (target <= this.stepIndex || this.stepsValidUpTo(target)) {
        this.stepIndex = target;
        this.render();
        this.focusStep();
      }
      return;
    }

    var remove = event.target.closest('[data-bb-remove]');
    if (remove && this.contains(remove)) {
      var removeStep = remove.getAttribute('data-bb-remove');
      var removeVariant = remove.getAttribute('data-bb-remove-variant');
      this.selection[removeStep] = (this.selection[removeStep] || []).filter(function (entry) {
        return String(entry.variantId) !== String(removeVariant);
      });
      this.error = '';
      this.render();
      return;
    }

    var chip = event.target.closest('[data-bb-filter]');
    if (chip && this.contains(chip)) {
      this.filters[step.id][Number(chip.getAttribute('data-bb-filter'))] =
        chip.getAttribute('data-bb-value') || '';
      this.render();
      return;
    }

    var card = event.target.closest('[data-bb-product]');
    if (!card || !this.contains(card)) {
      if (event.target.closest('[data-bb-add]')) this.addToCart();
      return;
    }

    var productId = card.getAttribute('data-bb-product');
    var product = step.products.filter(function (item) {
      return String(item.id) === String(productId);
    })[0];
    if (!product) return;

    var swatch = event.target.closest('[data-bb-option]');
    if (swatch) {
      this.chooseOption(step, product, Number(swatch.getAttribute('data-bb-option')),
        swatch.getAttribute('data-bb-value'));
      this.render();
      return;
    }

    /* En tarjetas por variante manda el id de la tarjeta. */
    var cardVariantId = card.getAttribute('data-bb-variant');
    var variant = null;
    if (step.cardLevel === 'variant' && cardVariantId) {
      variant = product.variants.filter(function (item) {
        return String(item.id) === String(cardVariantId);
      })[0] || null;
    }
    if (!variant) variant = this.currentVariant(product, step);
    if (!variant) return;

    var qtyBtn = event.target.closest('[data-bb-qty]');
    if (qtyBtn) {
      this.setQty(step, product, variant,
        this.qtyFor(step.id, variant.id) + Number(qtyBtn.getAttribute('data-bb-qty')));
      this.render();
      this.advanceIfDone(step);
      return;
    }

    if (!this.usesSteppers(step)) {
      this.toggleProduct(step, product, variant);
      this.render();
      this.advanceIfDone(step);
    }
  };

  /* En bundles por pasos, completar un paso lleva al siguiente sin clic extra. */
  proto.advanceIfDone = function (step) {
    if (this.config.tiers.length || this.config.steps.length < 2) return;
    if (step.max !== 1 || !this.isStepValid(step)) return;
    if (this.stepIndex >= this.config.steps.length - 1) return;
    var self = this;
    window.setTimeout(function () {
      if (self.config.steps[self.stepIndex] !== step) return;
      self.stepIndex += 1;
      self.render();
      self.focusStep();
    }, 260);
  };

  proto.stepsValidUpTo = function (target) {
    for (var i = 0; i < target; i++) {
      if (!this.isStepValid(this.config.steps[i])) return false;
    }
    return true;
  };

  proto.focusStep = function () {
    var node = this.querySelector('.bb__step-title');
    if (!node) return;
    node.setAttribute('tabindex', '-1');
    node.focus({ preventScroll: true });

    /* Cambiar de paso cambia el alto de la página de golpe: con 34 agendas el
       paso mide miles de píxeles y el siguiente mucho menos. Sin recolocar la
       vista, el cliente se encuentra de pronto mirando el pie de página.
       Solo se mueve si el nuevo paso ha quedado fuera de pantalla. */
    var box = node.getBoundingClientRect();
    if (box.top < 0 || box.top > window.innerHeight * 0.6) {
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var destino = Math.max(0, window.pageYOffset + box.top - 24);
      try {
        window.scrollTo({ top: destino, behavior: reduce ? 'auto' : 'smooth' });
      } catch (err) {
        window.scrollTo(0, destino);
      }
      /* Hay navegadores que ignoran la forma con objeto: si poco después la
         vista sigue donde estaba, se salta sin animación. */
      window.setTimeout(function () {
        if (Math.abs(window.pageYOffset - destino) > 80) window.scrollTo(0, destino);
      }, 350);
    }
  };

  /* ---------- carrito ---------- */

  /* Agrupa cantidades del mismo variantId en una sola línea de carrito. */
  proto.buildItems = function () {
    var self = this;
    var map = Object.create(null);
    var order = [];

    this.config.steps.forEach(function (step) {
      self.selection[step.id].forEach(function (entry) {
        var plan = entry.sellingPlanId || '';
        var key = String(entry.variantId) + '|' + plan;
        if (!map[key]) {
          var properties = {
            _bundle: self.config.name || 'Bundle',
            _bundle_ref: self.bundleRef,
            _bundle_slot: step.itemLabel || step.title || step.label || ''
          };
          /* Sin guion bajo: esta el cliente tiene que verla en el carrito,
             en el correo de confirmación y en el pedido. Cuando la línea lleva
             el plan de preordine, la propia app escribe su aviso: repetirlo
             solo ensucia el carrito. */
          if (entry.preorder && !entry.sellingPlanId && self.strings.preorderProperty) {
            properties[self.strings.preorderProperty] = self.strings.preorderPropertyValue;
          }
          map[key] = {
            id: entry.variantId,
            quantity: 0,
            handle: entry.productHandle,
            sellingPlanId: entry.sellingPlanId || null,
            properties: properties
          };
          order.push(key);
        }
        map[key].quantity += entry.qty;
      });
    });

    return order.map(function (key) { return map[key]; });
  };

  /* Revalida contra el catálogo vivo. OJO: /products/{handle}.js solo devuelve
     'available'; no trae inventory_quantity ni inventory_policy. Lo que aquí se
     comprueba es que la variante siga siendo vendible, no cuántas quedan: de la
     cantidad responde /cart/add.js, que es atómico y devuelve 422. */
  proto.validateLive = function (items) {
    var handles = unique(items.map(function (item) { return item.handle; })
      .filter(function (handle) { return !!handle; }));
    if (!handles.length) return Promise.resolve(true);

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    return Promise.all(handles.map(function (handle) {
      return fetchWithTimeout(root + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.ok ? res.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      var live = Object.create(null);
      products.forEach(function (product) {
        if (!product || !product.variants) return;
        product.variants.forEach(function (variant) { live[String(variant.id)] = variant; });
      });

      for (var i = 0; i < items.length; i++) {
        var fresh = live[String(items[i].id)];
        if (!fresh) continue; /* sin dato fresco: que decida /cart/add.js */
        if (fresh.available === false) return false;
        var tracked = fresh.inventory_management === 'shopify';
        var denies = fresh.inventory_policy === 'deny';
        var qty = Number(fresh.inventory_quantity);
        if (tracked && denies && isFinite(qty) && items[i].quantity > qty) return false;
      }
      return true;
    });
  };

  proto.addToCart = function () {
    if (this.isSubmitting || !this.isComplete()) return;

    var self = this;
    var items = this.buildItems();
    if (!items.length) return;

    this.isSubmitting = true;
    this.error = '';
    this.notice = '';
    this.render();

    this.validateLive(items)
      .then(function (ok) {
        if (!ok) throw new Error(self.strings.stockChanged);
        return self.applyDiscount();
      })
      .then(function () {
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        var sections = self.detectSections();
        var payload = {
          items: items.map(function (item) {
            var line = { id: item.id, quantity: item.quantity, properties: item.properties };
            if (item.sellingPlanId) line.selling_plan = item.sellingPlanId;
            return line;
          })
        };
        if (sections.length) {
          payload.sections = sections;
          payload.sections_url = window.location.pathname;
        }
        return fetchWithTimeout(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        }).then(function (res) {
          return readJson(res).then(function (data) {
            if (!res.ok) {
              throw new Error(data.description || data.message || self.strings.genericError);
            }
            return data;
          });
        });
      })
      .then(function (data) {
        self.isSubmitting = false;
        self.notice = self.strings.added;

        var addedRef = self.bundleRef;
        /* El bundle ya está en el carrito: la selección deja de estar viva.
           Si se quedara, pulsar otra vez volvería a añadirlo entero con el
           mismo identificador y el cliente compraría dos sin querer. */
        self.config.steps.forEach(function (step) {
          self.selection[step.id] = [];
          /* Vaciar el objeto entero dejaba sin su mapa a cada paso y
             currentVariant reventaba al repintar. */
          self.variantChoice[step.id] = {};
        });
        self.bundleRef = uid('bundle');
        self.stepIndex = 0;

        /* Si el repintado fallara, el bundle ya está dentro: no debe salir un
           error que invite al cliente a repetir la compra. */
        try {
          self.render();
          self.refreshCart(data);
        } catch (err) {
          if (window.console && console.warn) console.warn('[bundle-builder]', err);
        }

        document.dispatchEvent(new CustomEvent('bundle:added', {
          detail: { bundle: self.config.name, ref: addedRef, items: items }
        }));
      })
      .catch(function (err) {
        self.isSubmitting = false;
        self.error = (err && err.message) ? err.message : self.strings.genericError;
        self.render();
      });
  };

  proto.applyDiscount = function () {
    var code = this.config.discountCode;
    if (!code) return Promise.resolve();
    return fetchWithTimeout('/discount/' + encodeURIComponent(code), { method: 'GET' })
      .catch(function () { /* el descuento no debe bloquear la venta */ });
  };

  proto.cartHost = function () {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification') || null;
  };

  /* Cada tema pide sus propias secciones. En vez de adivinar los identificadores,
     se los preguntamos al componente de carrito del tema: así el aviso o el drawer
     se refrescan igual que en un "aggiungi al carrello" nativo. */
  proto.detectSections = function () {
    var ids = [];
    var host = this.cartHost();
    if (host && typeof host.getSectionsToRender === 'function') {
      try {
        host.getSectionsToRender().forEach(function (section) {
          if (section && section.id) ids.push(section.id);
        });
      } catch (err) { /* si el tema falla, se usa el respaldo de abajo */ }
    }
    if (!ids.length) {
      ['cart-drawer', 'cart-notification', 'cart-icon-bubble'].forEach(function (id) {
        if (document.getElementById('shopify-section-' + id) || document.querySelector(id)) ids.push(id);
      });
    }
    return unique(ids);
  };

  proto.refreshCart = function (data) {
    if ((this.config.afterAdd || 'drawer') === 'cart') {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      window.location.href = root + 'cart';
      return;
    }
    try {
      var host = this.cartHost();
      if (host && typeof host.renderContents === 'function' && data && data.sections) {
        /* El aviso de Dawn se dibuja a partir de la clave de UNA línea. Al añadir
           un bundle hay varias y esa clave no viene: se le pasa la primera para
           que el aviso abra en vez de quedarse vacío. El contador del carrito
           refleja igualmente el total. */
        if (!data.key && data.items && data.items.length) data.key = data.items[0].key;
        host.classList.remove('is-empty');
        host.renderContents(data);
        return;
      }
      this.updateBubble();
    } catch (err) {
      console.warn('[bundle-builder] no se pudo refrescar el carrito', err);
      this.updateBubble();
    }
  };

  proto.updateBubble = function () {
    fetchWithTimeout('/?sections=cart-icon-bubble')
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (!data || !data['cart-icon-bubble']) return;
        var target = document.getElementById('cart-icon-bubble');
        var parsed = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html')
          .querySelector('.shopify-section');
        if (target && parsed) target.innerHTML = parsed.innerHTML;
      })
      .catch(function () { /* silencioso */ });
  };

  /* ---------- registro ---------- */

  if (!window.customElements.get('bundle-builder')) {
    window.customElements.define('bundle-builder', BundleBuilder);
  }

  document.addEventListener('shopify:section:load', function (event) {
    var node = event.target.querySelector('bundle-builder');
    if (node && !node.initialized && typeof node.connectedCallback === 'function') {
      node.connectedCallback();
    }
  });

  window.BundleBuilderUtils = {
    formatMoney: formatMoney,
    maxQtyForVariant: maxQtyForVariant,
    handleize: handleize
  };
})();
